'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role?: string;
  farmerAccount?: {
    status: 'none' | 'pending' | 'approved' | 'rejected';
    farmName: string;
    farmAddress: string;
    farmSize: string;
    mainCrops: string[];
    certificationNumber: string;
    applicationDate: string | null;
    approvalDate: string | null;
  };
}

interface AuthContextType {
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a MockAuthProvider');
  }
  return context;
};

interface MockAuthProviderProps {
  children: React.ReactNode;
}

export const MockAuthProvider: React.FC<MockAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 로컬스토리지에서 사용자 정보 복원
    const savedUser = localStorage.getItem('mock-user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  const login = (userData: User) => {
    // Google OAuth 목 데이터와 연동하여 농업인 계정 정보 추가
    let enhancedUserData = { ...userData };
    
    // farmer@gangwon.kr 계정인 경우 농업인 정보 추가
    if (userData.email === 'farmer@gangwon.kr') {
      enhancedUserData = {
        ...userData,
        id: 'farmer-1',
        name: '농업인',
        avatar: '👨‍🌾',
        farmerAccount: {
          status: 'approved' as const,
          farmName: '강원도 특산물 농장',
          farmAddress: '강원도 춘천시 농장로 456',
          farmSize: '3헥타르',
          mainCrops: ['사과', '배', '버섯'],
          certificationNumber: 'FARM-2024-003',
          applicationDate: '2024-01-01',
          approvalDate: '2024-01-05'
        }
      };
    }
    // admin@foodfit.kr 계정인 경우 관리자 정보 추가
    else if (userData.email === 'admin@foodfit.kr') {
      enhancedUserData = {
        ...userData,
        id: 'admin-1',
        name: '강원푸드핏',
        avatar: '🍽️',
        role: 'admin'
      };
    }
    
    setUser(enhancedUserData);
    localStorage.setItem('mock-user', JSON.stringify(enhancedUserData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('mock-user');
  };

  const value = {
    user,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};